import { withClass, createPropFilter } from 'common/emotion'

import { breakWords } from 'component-library/__styling/style-mixins'
import { colors, metrics } from 'component-library/__styling/settings'

import { StyledLink as CardTextInteractions } from 'component-library/cards/CardText/CardTextInteractions.styled'
import {
    calcRem,
    breakpointMax,
    breakpointBetween,
    breakpoint,
} from 'component-library/__styling/style-functions'
import { themedValue } from 'component-library/__styling/themed-value'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { CardText } from '../CardText/CardText'
import { CardMedia, MediaMode } from '../CardMedia/CardMedia'
import { styledHeadlineTextClassName, StyledTeaser } from '../CardText/CardText.styled'
import { playButtonIconClass } from 'component-library/buttons/PlayButton/PlayButton.styled'
import { CardByline } from '../CardByline/CardByline'
import {
    StyledHeadshotWrapperClassName,
    StyledBylineClassName,
} from '../CardByline/CardByline.styled'
import { CardOrientationOptions } from './Portrait'
import { SponsoredLabel } from 'component-library/advertising/SponsoredLabel/SponsoredLabel'
import styled from '@emotion/styled'
import { WebLinkProps } from 'common/navigation/web-link'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

export const styledPortraitClassName = 'PortraitCard'

export interface StyledPortraitCardProps {
    hasBackground?: boolean
    hasSeparator?: boolean
    hasPadding?: boolean
    mediaMode?: MediaMode
    verticalSpacing?: keyof ThemeMargins
    isLarge?: boolean
    cardOrientation?: CardOrientationOptions
    fullWidthOnMobile?: boolean
}

export const StyledPortrait = withClass(styledPortraitClassName)(
    styled('div')<StyledPortraitCardProps>(
        {
            ...breakWords,
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            flexGrow: 1,
            alignItems: 'stretch',
        },
        ({ verticalSpacing, theme, hasBackground, fullWidthOnMobile, hasSeparator }) => ({
            marginBottom: verticalSpacing && theme.margins[verticalSpacing],
            fontSize: themedValue(theme, {
                thewest: calcRem(13),
                fallback: calcRem(14),
            }),
            borderBottom: themedValue(theme, {
                perthnow: `1px solid ${colors.perthnow.greyMarble}`,
                sevennews: hasSeparator ? `1px solid ${colors.sevennews.greyMarble}` : undefined,
                fallback: undefined,
            }),
            backgroundColor: themedValue(theme, {
                perthnow: hasBackground ? colors.perthnow.greyBasalt : colors.white,
                thewest: hasBackground ? colors.white : 'transparent',
                sevennews: hasBackground ? colors.black : 'transparent',
            }),

            [breakpointMax('sm')]: {
                width: fullWidthOnMobile
                    ? `calc(100% + ${theme.margins.outerMargin * 2}px)`
                    : undefined,
                margin: fullWidthOnMobile ? calcRem(0, -theme.margins.outerMargin) : undefined,
                marginBottom: verticalSpacing && theme.margins[verticalSpacing],
            },
        })
    )
)

export interface StyledLinkProps {
    isLarge?: boolean
    hasBackground?: boolean
    cardOrientation?: CardOrientationOptions
    hasPadding?: boolean
    fullWidthOnMobile?: boolean
    isSponsored?: boolean
}

export const StyledLink = styled(CardTextInteractions, {
    shouldForwardProp: createPropFilter<WebLinkProps>()([
        'isLarge',
        'hasBackground',
        'cardOrientation',
        'hasPadding',
        'fullWidthOnMobile',
        'isSponsored',
    ]),
})<StyledLinkProps>(
    {
        textDecoration: 'none',
        display: 'flex',
        flexFlow: 'column nowrap',
        width: '100%',
        textDecorationSkipInk: 'auto',
    },
    ({
        isLarge,
        hasBackground,
        theme,
        cardOrientation,
        hasPadding,
        fullWidthOnMobile,
        isSponsored,
    }) => ({
        padding: themedValue(theme, {
            thewest: undefined,
            sevennews: hasPadding ? calcRem(theme.cards.outerMarginLarge) : undefined,
            fallback: hasPadding ? calcRem(theme.cards.outerMargin) : undefined,
        }),
        paddingBottom: themedValue(theme, {
            perthnow: calcRem(theme.cards.outerMargin),
            fallback: undefined,
        }),

        [breakpoint('sm')]: {
            padding: themedValue(theme, {
                thewest: hasPadding ? calcRem(theme.cards.outerMargin) : undefined,
                perthnow: hasPadding ? calcRem(theme.cards.outerMargin) : undefined,
                fallback: undefined,
            }),

            ...conditionalStyles(isLarge && hasPadding && theme.kind === 'sevennews', {
                padding: calcRem(16),
            }),
        },

        // Add padding in to the card so that the text doesn't hit up against the edge
        ...conditionalStyles(fullWidthOnMobile && !hasPadding, {
            [breakpointMax('sm')]: {
                padding: themedValue(theme, {
                    sevennews: isLarge ? calcRem(16) : calcRem(theme.cards.outerMargin),
                    fallback: calcRem(theme.cards.outerMargin),
                }),
            },
        }),

        // want to give the portrait card a bigger focus border when isLarge because of
        // having a black background on sevennews when hasBackground is set
        '&:focus': {
            outline:
                isLarge && hasBackground && theme.kind === 'sevennews'
                    ? `solid 3px ${colors.sevennews.blueRibbon}`
                    : undefined,
        },

        [`& .${playButtonIconClass}`]: {
            opacity: isLarge ? 1 : 0,
        },

        [`&:hover .${playButtonIconClass}, &:focus .${playButtonIconClass}`]: {
            opacity: 1,
        },

        ...(cardOrientation && cardOrientation.type === 'Landscape-Portrait'
            ? {
                  [breakpointMax(cardOrientation.maxBreakpoint || 'sm')]: {
                      flexFlow: 'row wrap',
                      paddingBottom: isSponsored ? theme.margins.sm : undefined,

                      [`& .${StyledBylineClassName}`]: {
                          display: isSponsored ? 'block' : 'none',
                      },
                  },
              }
            : {}),
    })
)
StyledLink.displayName = 'StyledLink'

interface StyledCardMediaProps {
    isLarge?: boolean
    hasBackground?: boolean
    cardOrientation?: CardOrientationOptions
    mediaMode?: MediaMode
    fullWidthOnMobile?: boolean
}

const mobileImageWidth = 110
const tabletImageWidth = 210

export const StyledCardMedia = styled(CardMedia)<StyledCardMediaProps>(
    ({ theme, cardOrientation, fullWidthOnMobile, mediaMode }) => ({
        // Different image widths based on the Breakpoint for the card
        ...(cardOrientation && cardOrientation.type === 'Landscape-Portrait'
            ? {
                  [breakpointMax('sm')]: {
                      width: mobileImageWidth,
                      alignSelf: 'flex-start', //ensures image container does not stretch to height of text column breaking interaction states
                      margin: calcRem(0, theme.margins.sm, 0, 0),
                  },
              }
            : {}),

        ...(cardOrientation &&
        cardOrientation.type === 'Landscape-Portrait' &&
        cardOrientation.maxBreakpoint === 'md'
            ? {
                  [breakpointBetween('sm', 'md')]: {
                      width: tabletImageWidth,
                      marginRight: calcRem(theme.margins.md),
                  },
              }
            : {}),

        ...conditionalStyles(mediaMode === 'edgeToEdge' && theme.kind === 'sevennews', {
            [breakpoint('sm')]: {
                width: `calc(100% + ${metrics.sevennews.cards.outerMarginLarge * 2}px)`,
                margin: calcRem(
                    -metrics.sevennews.cards.outerMarginLarge,
                    -metrics.sevennews.cards.outerMarginLarge,
                    metrics.sevennews.margins.sm,
                    -metrics.sevennews.cards.outerMarginLarge
                ),
            },
        }),

        // Some of the cards on SevenNews need to be full width on Mobile but also wont have padding on tablet or desktop
        // This will ensure those cards still render correctly
        ...conditionalStyles(fullWidthOnMobile && theme.kind === 'sevennews', {
            [breakpointMax('sm')]: {
                width: `calc(100% + ${metrics.sevennews.cards.outerMarginLarge * 2}px)`,
                margin: calcRem(
                    -metrics.sevennews.cards.outerMarginLarge,
                    -metrics.sevennews.cards.outerMarginLarge,
                    metrics.sevennews.margins.sm,
                    -metrics.sevennews.cards.outerMarginLarge
                ),
            },
        }),
    })
)

interface StyledCardTextProps {
    cardOrientation?: CardOrientationOptions
    fullWidthOnMobile?: boolean
}

export const StyledCardText = styled(CardText)<StyledCardTextProps>(
    ({ theme, cardOrientation, fontScale }) => ({
        [`& .${styledHeadlineTextClassName}`]: {
            [breakpointMax('sm')]: {
                fontSize: theme.kind === 'thewest' ? calcRem(16) : undefined, // Make all portrait card font sizes the same on smaller screens
            },
        },

        // Breakpoints don't collapse, so if the maxBreakpoint is sm hiding the teaser wont work.
        // TODO: resolve the teaser issue another way - potentially with breakpoint objects on hideTeaser props
        ...(cardOrientation && cardOrientation.type === 'Landscape-Portrait'
            ? {
                  [breakpointMax('sm')]: {
                      width: `calc(100% - (${mobileImageWidth + theme.margins.md}px))`,
                      fontSize: theme.kind === 'thewest' ? calcRem(16) : undefined, // Make all portrait card font sizes the same on smaller screens

                      [`& .${StyledTeaser}`]: {
                          display: 'none',
                      },
                  },
              }
            : {}),

        ...(cardOrientation &&
        cardOrientation.type === 'Landscape-Portrait' &&
        cardOrientation.maxBreakpoint === 'md'
            ? {
                  // Stops breakpoints being overwritten
                  [breakpointBetween('sm', 'md')]: {
                      width: `calc(100% - (${tabletImageWidth + theme.margins.md}px))`,

                      [`& .${styledHeadlineTextClassName}`]: {
                          fontSize: fontScale && calcRem(Math.ceil(18 * fontScale)),
                      },
                  },
              }
            : {}),
    })
)
export interface StyledCardBylineProps {
    cardOrientation?: CardOrientationOptions
    hasPadding?: boolean
    mediaMode?: MediaMode
}
export const StyledCardByline = styled(CardByline)<StyledCardBylineProps>(
    ({ cardOrientation, theme, hasPadding, mediaMode }) => ({
        ...(cardOrientation && cardOrientation.type === 'Landscape-Portrait'
            ? {
                  [breakpointMax('sm')]: {
                      marginLeft: themedValue(theme, {
                          thewest:
                              mediaMode === 'default'
                                  ? calcRem(mobileImageWidth + theme.margins.sm)
                                  : undefined,
                          fallback: undefined,
                      }),
                      marginTop: themedValue(theme, {
                          thewest: hasPadding ? calcRem(theme.margins.xs) : 0,
                          fallback: undefined,
                      }),
                      padding: themedValue(theme, {
                          thewest: 0,
                          fallback: undefined,
                      }),
                      // If byline contains a headshot, hide the headshot on mobile
                      [`& .${StyledHeadshotWrapperClassName}`]: {
                          display: 'none',
                      },
                  },
              }
            : {}),

        ...(cardOrientation &&
        cardOrientation.type === 'Landscape-Portrait' &&
        cardOrientation.maxBreakpoint === 'md'
            ? {
                  [breakpointBetween('sm', 'md')]: {
                      marginLeft: themedValue(theme, {
                          thewest: calcRem(tabletImageWidth + theme.margins.md),
                          fallback: undefined,
                      }),
                  },
              }
            : {}),
    })
)

export const StyledSponsoredLabel = styled(SponsoredLabel)(({ theme }) => ({
    marginTop: theme.kind === 'perthnow' ? calcRem(18) : undefined,
}))
